import { useEffect, useState } from 'react';
import API_CONFIG from '../config/api';

// Built-in brand: used until the server's copy arrives, and whenever it can't.
export const LOGIN_BRANDING_DEFAULTS = Object.freeze({
    brandName: 'FinMudra',
    legalName: 'FinMudra Credit Pvt Ltd',
    welcomeTitle: 'Welcome back',
    tagline: 'Sign in to manage loans, disbursals, and collections.',
    emailDomain: 'finmudra.in',
    footerNote: 'internal staff only.',
    superTagline: 'Enterprise gateway for system configuration and oversight',
    accentColor: '#c9962c',
    pageColor: '',
    cardColor: '',
    template: 'passbook',
    logoUrl: null,
    logoUploadedAt: null,
    faviconUrl: null,
    faviconUploadedAt: null
});

export const DEFAULT_LOGIN_LOGO = `${process.env.PUBLIC_URL || ''}/finmudra_icon.png`;
export const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon-optimized.png`;

const CACHE_KEY = 'loginBranding.v2';

const readCache = () => {
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};
const writeCache = (data) => {
    try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) { /* private mode: nothing to keep */ }
};

// Image URLs come back API-relative ("/api/public/branding-image/logo?v=…"),
// since the bytes are served by the backend from the database; they are made
// absolute against the configured API base here.
const absoluteApiUrl = (url) => (url && url.startsWith('/') ? `${API_CONFIG.BASE_URL}${url}` : url || null);

export async function fetchLoginBranding() {
    const res = await fetch(`${API_CONFIG.BASE_URL}/api/public/login-branding`);
    const body = await res.json();
    if (!res.ok || !body.success || !body.data) throw new Error(body.message || 'Branding unavailable');
    return {
        ...LOGIN_BRANDING_DEFAULTS,
        ...body.data,
        logoUrl: absoluteApiUrl(body.data.logoUrl),
        faviconUrl: absoluteApiUrl(body.data.faviconUrl)
    };
}

// Every page of the admin app reads the brand through this — the sign-in
// pages, the sidebar after login, the browser tab. The last copy seen in this
// browser is used first, so a renamed portal never flashes the built-in name
// while the request is in flight; the server's answer then replaces it. The
// images are held back until that answer lands (`ready`), so a mark that has
// since been replaced or removed is never shown from a stale cache.
export function useBranding() {
    const [branding, setBranding] = useState(() => ({
        ...LOGIN_BRANDING_DEFAULTS, ...(readCache() || {}), logoUrl: null, faviconUrl: null
    }));
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchLoginBranding()
            .then((data) => {
                if (cancelled) return;
                setBranding(data);
                writeCache({ ...data, logoUrl: null, faviconUrl: null });
            })
            .catch(() => { /* the built-in or cached brand stays up */ })
            .finally(() => { if (!cancelled) setReady(true); });
        return () => { cancelled = true; };
    }, []);

    return {
        ...branding,
        ready,
        logoSrc: branding.logoUrl || DEFAULT_LOGIN_LOGO,
        // No dedicated favicon uploaded → the logo stands in, so a rebranded
        // portal does not keep the old mark in its tab.
        faviconSrc: branding.faviconUrl || branding.logoUrl || DEFAULT_FAVICON
    };
}

// Kept for the pages written against the earlier name.
export const useLoginBranding = useBranding;

// Browser-tab title and icon. The icon link is swapped only once the server
// has answered, so a stale cached URL never replaces the built-in favicon with
// a broken one.
export function useDocumentBranding(brand, title) {
    useEffect(() => {
        if (title) document.title = title;
    }, [title]);
    useEffect(() => {
        if (!brand.ready) return;
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        if (link.getAttribute('href') !== brand.faviconSrc) link.setAttribute('href', brand.faviconSrc);
    }, [brand.ready, brand.faviconSrc]);
}

// The sidebar shows the brand on one line and the rest of the company name
// under it: "FINMUDRA" / "CREDIT PVT LTD". When the company name does not
// start with the brand, the whole company name goes on the second line.
export const legalSubline = (brandName, legalName) => {
    const brand = String(brandName || '').trim();
    const legal = String(legalName || '').trim();
    if (!legal) return '';
    if (brand && legal.toLowerCase().startsWith(brand.toLowerCase())) {
        return legal.slice(brand.length).replace(/^[\s,.-]+/, '').trim();
    }
    return legal;
};

// Lighten (amount > 0) or darken (amount < 0) a #rrggbb colour by mixing it
// toward white or black. Anything that is not a six-digit hex comes back as is.
export const shadeHex = (hex, amount) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return hex;
    const target = amount < 0 ? 0 : 255;
    const t = Math.min(1, Math.abs(Number(amount) || 0));
    const channels = [0, 2, 4].map((i) => {
        const c = parseInt(m[1].slice(i, i + 2), 16);
        return Math.round(c + (target - c) * t).toString(16).padStart(2, '0');
    });
    return `#${channels.join('')}`;
};

export const isHexColor = (value) => /^#[0-9a-f]{6}$/i.test(String(value || '').trim());

const DEFAULT_ACCENT = LOGIN_BRANDING_DEFAULTS.accentColor;

const hexToRgb = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return [0, 0, 0];
    return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
};
const rgbString = (hex) => hexToRgb(hex).join(', ');

// WCAG relative luminance, enough to tell a dark card from a light one so
// the text on it can flip to white.
const luminance = (hex) => {
    const [r, g, b] = hexToRgb(hex).map((c) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const isDark = (hex) => luminance(hex) < 0.4;

// Text that sits on the accent itself (buttons, the signboard band): navy or
// white, whichever contrasts more with it. The two tie at a luminance of
// about 0.22, so a mid-tone such as the built-in gold keeps navy text, and
// only a genuinely dark accent (a navy, a maroon) gets white.
export const onAccent = (accent) => (luminance(isHexColor(accent) ? accent : DEFAULT_ACCENT) < 0.22 ? '#ffffff' : '#1a2744');

// The hand-tuned "passbook" palette of the staff sign-in page.
export const DEFAULT_PALETTE = Object.freeze({
    page: '#fbf6ea', pageEnd: '#f3ead6',
    card: '#f6efdc', cardEnd: '#fbf6ea', field: '#fbf6ea',
    line: '#ddcfa3', ink: '#1a2744', inkSoft: '#6b7590',
    seal: '#fffdf7', sealEnd: '#f3e8cc'
});

// The page's surfaces follow the brand. With only an accent set, the paper,
// card, borders and seal are the accent washed almost to white, so a pink
// accent gets a blush page rather than the gold-tinted cream it ships with.
// An explicit page or card colour wins over that; a dark card flips its text
// to white. The built-in accent with no overrides returns the palette as is.
export const derivePalette = (accent, { pageColor, cardColor } = {}) => {
    const a = isHexColor(accent) ? accent.toLowerCase() : DEFAULT_ACCENT;
    const customAccent = a !== DEFAULT_ACCENT;
    const page = isHexColor(pageColor) ? pageColor.toLowerCase() : null;
    const card = isHexColor(cardColor) ? cardColor.toLowerCase() : null;
    if (!customAccent && !page && !card) return { ...DEFAULT_PALETTE, accent: a, custom: false };

    const p = { ...DEFAULT_PALETTE, accent: a, custom: true };
    if (customAccent) {
        p.page = shadeHex(a, 0.94); p.pageEnd = shadeHex(a, 0.88);
        p.card = shadeHex(a, 0.9); p.cardEnd = shadeHex(a, 0.95); p.field = shadeHex(a, 0.95);
        p.line = shadeHex(a, 0.68);
        p.seal = shadeHex(a, 0.97); p.sealEnd = shadeHex(a, 0.86);
    }
    if (page) {
        p.page = page; p.pageEnd = shadeHex(page, isDark(page) ? 0.08 : -0.05);
        if (!card) {
            // The card stays a shade lighter than the page so it still reads as a card.
            p.card = isDark(page) ? shadeHex(page, 0.1) : shadeHex(page, 0.45);
            p.cardEnd = isDark(page) ? shadeHex(page, 0.16) : shadeHex(page, 0.7);
            p.field = isDark(page) ? shadeHex(page, 0.2) : shadeHex(page, 0.7);
        }
    }
    if (card) {
        p.card = card; p.cardEnd = shadeHex(card, isDark(card) ? 0.08 : 0.35);
        p.field = isDark(card) ? shadeHex(card, 0.12) : shadeHex(card, -0.04);
    }
    if (isDark(p.card)) {
        p.ink = '#ffffff'; p.inkSoft = 'rgba(255, 255, 255, 0.72)';
        p.line = 'rgba(255, 255, 255, 0.22)';
        p.seal = shadeHex(p.card, 0.18); p.sealEnd = shadeHex(p.card, 0.06);
    }
    return p;
};

// ── Sign-in page layouts ────────────────────────────────────────────────────
// The five looks a brand can give its staff sign-in page (Settings → Branding
// → Login page design). The key is what system_settings stores (login_template,
// checked by backend/lib/loginBranding.js); the layouts themselves live in
// pages/loginLayouts.js and their styles in pages/AdminLoginTemplates.css.
// Every layout takes the same wording, logo and colours, so switching never
// loses the rest of the branding.
export const LOGIN_TEMPLATES = Object.freeze([
    { key: 'passbook', label: 'Passbook', blurb: 'A warm card with the logo in a coin seal and an engraved pattern behind it.', dark: false, hasCard: true },
    { key: 'split', label: 'Split panel', blurb: 'The brand on a tall dark panel to the left, the form on white to the right.', dark: false, hasCard: true },
    { key: 'minimal', label: 'Ledger', blurb: 'No card at all: a plain page ruled like an accounts register, with the logo top left.', dark: false, hasCard: false },
    { key: 'midnight', label: 'Midnight', blurb: 'A dark page under a soft spotlight, with a solid card and the logo in a tile.', dark: true, hasCard: true },
    { key: 'horizon', label: 'Signboard', blurb: 'A wide band in the accent colour across the top, with the form card hanging over its edge.', dark: false, hasCard: true }
]);
export const LOGIN_TEMPLATE_KEYS = LOGIN_TEMPLATES.map((t) => t.key);
export const DEFAULT_LOGIN_TEMPLATE = 'passbook';
export const normalizeTemplate = (key) => (LOGIN_TEMPLATE_KEYS.includes(String(key || '').toLowerCase()) ? String(key).toLowerCase() : DEFAULT_LOGIN_TEMPLATE);
export const templateMeta = (key) => LOGIN_TEMPLATES.find((t) => t.key === normalizeTemplate(key));

// What each layout's surfaces are on Auto (no page or card colour set). The
// passbook keeps its hand-tuned cream in DEFAULT_PALETTE; the rest are plain
// neutrals so the accent and the brand's own images carry the colour.
const TEMPLATE_SURFACES = Object.freeze({
    split: { page: '#f3f5f9', pageEnd: '#e8ecf3', card: '#ffffff', cardEnd: '#ffffff', field: '#f6f7fa', line: '#dfe3eb', ink: '#141c2e', inkSoft: '#5d6678', shadowRgb: '20, 28, 46' },
    minimal: { page: '#ffffff', pageEnd: '#ffffff', card: '#ffffff', cardEnd: '#ffffff', field: '#ffffff', line: '#cfd3db', ink: '#111827', inkSoft: '#6b7280', shadowRgb: '17, 24, 39' },
    midnight: { page: '#0b1020', pageEnd: '#151d36', card: '#121a30', cardEnd: '#0e1527', field: '#0b1122', line: 'rgba(255, 255, 255, 0.14)', ink: '#f4f6ff', inkSoft: 'rgba(244, 246, 255, 0.62)', shadowRgb: '2, 6, 23' },
    horizon: { page: '#f5f6f9', pageEnd: '#eceef3', card: '#ffffff', cardEnd: '#ffffff', field: '#f6f7fa', line: '#e1e4eb', ink: '#151b2b', inkSoft: '#616a7f', shadowRgb: '21, 27, 43' }
});
const LIGHT_INK = { ink: '#141c2e', inkSoft: '#5d6678' };
const DARK_INK = { ink: '#f4f6ff', inkSoft: 'rgba(244, 246, 255, 0.62)', line: 'rgba(255, 255, 255, 0.14)' };

// The resolved surfaces of a layout: the template's own neutrals, an explicit
// page colour over those (a card-less layout takes it for the form area too,
// and a dark page lifts the card a shade above itself), an explicit card
// colour over that, and the text colour following whichever surface the
// form sits on. The passbook is derivePalette as before.
export const templatePalette = (template, accent, { pageColor, cardColor } = {}) => {
    const t = normalizeTemplate(template);
    const a = isHexColor(accent) ? accent.toLowerCase() : DEFAULT_ACCENT;
    if (t === DEFAULT_LOGIN_TEMPLATE) return derivePalette(a, { pageColor, cardColor });
    const hasCard = templateMeta(t).hasCard;
    const p = { ...TEMPLATE_SURFACES[t], accent: a, custom: true, template: t };
    const page = isHexColor(pageColor) ? pageColor.toLowerCase() : null;
    const card = hasCard && isHexColor(cardColor) ? cardColor.toLowerCase() : null;
    if (page) {
        p.page = page; p.pageEnd = shadeHex(page, isDark(page) ? 0.08 : -0.04);
        if (!hasCard) { p.card = page; p.cardEnd = page; p.field = page; }
        else if (!card) {
            p.card = isDark(page) ? shadeHex(page, 0.1) : '#ffffff';
            p.cardEnd = isDark(page) ? shadeHex(page, 0.16) : '#ffffff';
            p.field = isDark(page) ? shadeHex(page, 0.2) : '#f6f7fa';
        }
    }
    if (card) {
        p.card = card; p.cardEnd = shadeHex(card, isDark(card) ? 0.08 : 0);
        p.field = isDark(card) ? shadeHex(card, 0.12) : shadeHex(card, -0.03);
    }
    if (page || card) {
        if (isDark(p.card)) Object.assign(p, DARK_INK);
        else Object.assign(p, LIGHT_INK, { line: shadeHex(p.card, -0.12) });
    }
    return p;
};

// The accent-only tokens: the button, ring and pattern colours.
const accentTokens = (accent) => ({
    '--gold-500': accent,
    '--gold-600': shadeHex(accent, -0.2),
    '--gold-400': shadeHex(accent, 0.25),
    '--gold-300': shadeHex(accent, 0.45),
    '--gold-rgb': rgbString(accent),
    '--shadow-rgb': rgbString(shadeHex(accent, -0.45)),
    '--on-accent': onAccent(accent)
});

// The login stylesheet is written against a few CSS tokens (see :root in
// AdminLoginPage.css); a custom brand re-points them on the page root. The
// built-in brand leaves them alone, so the default page stays exactly the
// hand-tuned one.
export const accentStyle = (accent, options = {}) => {
    const p = derivePalette(accent, options);
    if (!p.custom) return {};
    const style = {
        '--paper-50': p.page, '--page-bg-end': p.pageEnd,
        '--card-bg-start': p.card, '--card-bg-end': p.cardEnd, '--field-bg': p.field,
        '--line-warm': p.line, '--ink-900': p.ink, '--ink-600': p.inkSoft,
        '--seal-50': p.seal, '--seal-100': p.sealEnd
    };
    if (p.accent !== DEFAULT_ACCENT) Object.assign(style, accentTokens(p.accent));
    return style;
};

// The tokens for whichever layout the brand picked. The passbook is
// accentStyle (nothing for the built-in brand); every other layout always
// gets its surfaces from templatePalette, so its stylesheet is written
// against the tokens alone and never needs a copy of the neutrals.
export const templateStyle = (template, accent, options = {}) => {
    const t = normalizeTemplate(template);
    if (t === DEFAULT_LOGIN_TEMPLATE) return accentStyle(accent, options);
    const p = templatePalette(t, accent, options);
    const style = {
        '--paper-50': p.page, '--page-bg-end': p.pageEnd,
        '--card-bg-start': p.card, '--card-bg-end': p.cardEnd, '--field-bg': p.field,
        '--line-warm': p.line, '--ink-900': p.ink, '--ink-600': p.inkSoft,
        '--shadow-rgb': p.shadowRgb,
        '--on-accent': onAccent(p.accent)
    };
    if (p.accent !== DEFAULT_ACCENT) Object.assign(style, accentTokens(p.accent), { '--shadow-rgb': p.shadowRgb });
    return style;
};

// Gradient stops for the engraved medallion behind the staff sign-in card.
export const medallionStops = (accent) => {
    if (!isHexColor(accent) || accent.toLowerCase() === LOGIN_BRANDING_DEFAULTS.accentColor) {
        return ['#b9832a', '#8a5f16'];
    }
    return [shadeHex(accent, -0.1), shadeHex(accent, -0.35)];
};
